/**
 * Worklist V8 and V9 additions (Aithora ConsoleV9.dc.html; v9/handoff.js, v9/care-ops.js,
 * v9/med-watch.js), worded from the live routes:
 *
 *   - "Pulled away" cover (GET /worklist/cover, /facilities/:id/worklist/coverage): what moved to
 *     me with Accept all and "Can't, pass on", what of mine moved with Take back, and the
 *     Facility Admin's one-line read-out.
 *   - New medicine watch (GET /facilities/:id/med-watches): the steps due (nurse, Facility Admin,
 *     read only for the Aithora team) and what to watch for (care staff).
 *   - Pick up where you left off: the Worklist tab this person was on, kept in this browser.
 *
 * What has no back end yet (the shift brief and who heard it, call-out fill, the medicine safety
 * cards, credentials and survey items) is a typed placeholder here that answers null, and the
 * page says plainly that it is not connected. Nothing here invents a number.
 *
 * Pure apart from the resume store: the page passes the API answers and the clock.
 */
import type { CoverGiven, CoverLive, CoverReceived, MedWatch, MedWatchSigns, TaskLadderView } from '@aithora/shared';

const M = 60_000;
const clock = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

// ── The cover words ─────────────────────────────────────────────────────────

/** The server's words without what happened after ("· passed on", "· not accepted in time"). */
function coverBase(words: string): string {
  return words.split(' · passed on')[0].split(' · not accepted in time')[0].trim();
}

/** "Covering for Jackie · Edna's emergency" as the moved item's tag: "From Jackie · Edna’s emergency". */
export function coverFrom(words: string): string {
  const b = coverBase(words).replace(/'/g, '’');
  return /^Covering for /.test(b) ? b.replace(/^Covering for /, 'From ') : b;
}

/** The colleague who was pulled away ("Jackie"), when the words name them. */
export function coverSender(words: string): string | null {
  const m = /^Covering for (.+?)(?: · |$)/.exec(coverBase(words));
  return m && m[1] !== 'a colleague' ? m[1] : null;
}

/** The resident whose emergency it is ("Edna"), when the words name them. */
export function coverResident(words: string): string | null {
  const m = /· (.+?)['’]s emergency/.exec(coverBase(words));
  return m ? m[1] : null;
}

/** When a moved item stops being on time: a dose at the end of its window, a task at its due time. */
export function closesAt(tab: 'tasks' | 'medpass', dueAt: string | null, windowMin: number | null): number | null {
  if (!dueAt) return null;
  const due = Date.parse(dueAt);
  if (!Number.isFinite(due)) return null;
  return due + (tab === 'medpass' ? (windowMin ?? 60) : 0) * M;
}

/** "due 12 min ago", "due now", "due in 10 min". */
export function dueWords(dueAt: string | null, now: number): string {
  if (!dueAt) return 'no due time';
  const d = Math.round((Date.parse(dueAt) - now) / M);
  return d < 0 ? `due ${-d} min ago` : d === 0 ? 'due now' : `due in ${d} min`;
}

/** "window closes in 12 min", or "window closed · late". */
export function windowWords(closes: number | null, now: number): string {
  if (closes === null) return '';
  const c = Math.round((closes - now) / M);
  return c <= 0 ? 'window closed · late' : `window closes in ${c} min`;
}

/** Why it went to whom it did (handoff.js WHY), from what the item is. */
export function coverWhy(tab: 'tasks' | 'medpass', windowMin: number | null): string {
  if (tab === 'tasks') return 'Care task';
  return windowMin !== null && windowMin <= 30 ? 'Time-critical · 30-min window' : 'Medicine · nurse or med aide';
}

/**
 * The late ladder as one simple line, from the server's ladder for the task (ladder-rule.ts):
 * "If late: You → charge nurse at 30 min → everyone on shift at 45". Null where the server runs
 * no ladder for the item (a dose on the med pass): the page then says nothing about one.
 */
export function ladderWords(owner: string, ladder: TaskLadderView | null | undefined): string | null {
  if (!ladder || !ladder.minutes.length || ladder.ladderOn === false) return null;
  const NAME: Record<string, string> = { charge_nurse: 'charge nurse', on_call_nurse: 'on-call nurse', everyone: 'everyone on shift' };
  const parts: string[] = [];
  let acc = 0;
  ladder.steps.forEach((s, i) => {
    if (i === 0) return;
    acc += ladder.minutes[i - 1] ?? 0;
    parts.push(i === 1 ? `${NAME[s] ?? s} at ${acc} min` : `${NAME[s] ?? s} at ${acc}`);
  });
  return parts.length ? `If late: ${owner} → ${parts.join(' → ')}` : null;
}

/** What the page knows about a moved item from its own lists (the cover read's own names come first). */
export interface CoverLookup {
  who: string | null;
  room: string | null;
  task: string | null;
  ladder?: TaskLadderView | null;
}

export interface CoverRowView {
  key: string;
  ref: CoverReceived['itemRef'];
  ini: string;
  who: string;
  task: string;
  due: string;
  win: string;
  hot: boolean;
  from: string;
  why: string;
  ladder: string | null;
  accepted: boolean;
}

export interface ReceiverCard {
  causeId: string;
  head: string;
  accOpen: boolean;
  /** Not yet accepted: what Accept all and "Can't, pass on" act on. */
  pending: CoverReceived['itemRef'][];
  rows: CoverRowView[];
  /** Minutes until the first unaccepted item passes on by itself (null when nothing is waiting). */
  passIn: number | null;
}

/** Minutes until an unaccepted item passes on (5, or 2 for time-critical), from when it moved. */
export function passesOnIn(r: CoverReceived, now: number): number | null {
  if (!r.movedAt || r.accepted) return null;
  const wait = r.timeCritical || (r.itemRef.tab === 'medpass' && (r.windowMin ?? 60) <= 30) ? 2 : 5;
  return Math.max(0, Math.ceil((Date.parse(r.movedAt) + wait * M - now) / M));
}

const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();
const refKey = (r: CoverReceived['itemRef']) => `${r.tab}:${r.sourceId}:${r.occurrence ?? ''}`;

/** One card per emergency for the person the work moved to (handoff.js rcv). */
export function receiverCards(received: CoverReceived[], look: (ref: CoverReceived['itemRef']) => CoverLookup, now: number): ReceiverCard[] {
  const by = new Map<string, CoverReceived[]>();
  for (const r of received) by.set(r.causeId, [...(by.get(r.causeId) ?? []), r]);
  return [...by.entries()].map(([causeId, items]) => {
    const sorted = items.slice().sort((a, b) => (closesAt(a.itemRef.tab, a.dueAt, a.windowMin) ?? Infinity) - (closesAt(b.itemRef.tab, b.dueAt, b.windowMin) ?? Infinity));
    const words = sorted[0].words;
    const sender = coverSender(words);
    const res = coverResident(words);
    const meds = sorted.every((x) => x.itemRef.tab === 'medpass');
    const n = sorted.length;
    const noun = meds ? (n === 1 ? 'med' : 'meds') : n === 1 ? 'task' : 'tasks';
    const first = closesAt(sorted[0].itemRef.tab, sorted[0].dueAt, sorted[0].windowMin);
    const firstMin = first === null ? null : Math.round((first - now) / M);
    const head =
      `${n} ${noun} from ${sender ?? 'a colleague'}` +
      (res ? ` · ${res}’s emergency` : '') +
      (firstMin === null ? '' : firstMin > 0 ? ` · first window closes in ${firstMin} min` : ' · a window has closed');
    const rows = sorted.map((x) => {
      const l0 = look(x.itemRef);
      const l = { ...l0, who: x.residentName ?? l0.who, task: x.task ?? l0.task };
      const closes = closesAt(x.itemRef.tab, x.dueAt, x.windowMin);
      const who = l.who ? `${l.who}${l.room ? ` · Rm ${l.room}` : ''}` : x.itemRef.tab === 'medpass' ? 'A resident' : 'A task';
      return {
        key: refKey(x.itemRef),
        ref: x.itemRef,
        ini: initials(l.who ?? '?'),
        who,
        task: l.task ?? (x.itemRef.tab === 'medpass' ? 'Medicine' : 'Task'),
        due: dueWords(x.dueAt, now),
        win: windowWords(closes, now),
        hot: closes !== null && closes - now <= 15 * M,
        from: coverFrom(x.words),
        why: coverWhy(x.itemRef.tab, x.windowMin),
        ladder: ladderWords('You', l.ladder),
        accepted: x.accepted,
      };
    });
    const pending = sorted.filter((x) => !x.accepted).map((x) => x.itemRef);
    const waits = sorted.map((x) => passesOnIn(x, now)).filter((v): v is number => v !== null);
    return { causeId, head, accOpen: pending.length > 0, pending, rows, passIn: waits.length ? Math.min(...waits) : null };
  });
}

/** The tag a moved item carries in its own list ("From Jackie · Edna’s emergency"), by row. */
export function coverTags(received: CoverReceived[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const r of received) out.set(`${r.itemRef.tab}:${r.itemRef.sourceId}`, coverFrom(r.words));
  return out;
}

export interface SenderItem {
  key: string;
  ref: CoverGiven['itemRef'];
  who: string;
  task: string;
  to: string;
  done: boolean;
  doneLine: string;
  open: boolean;
}

export interface SenderCard {
  causeId: string;
  head: string;
  backHead: string;
  items: SenderItem[];
  openN: number;
  /** The emergency is over (from the server): the card opens on "Back from the emergency". */
  over: boolean;
}

/** One card per emergency for the person pulled away (handoff.js hoHead / hoBackHead). */
export function senderCards(
  given: CoverGiven[],
  look: (ref: CoverGiven['itemRef']) => CoverLookup,
  holder: (g: CoverGiven) => string,
  wordsOf: Map<string, string>,
): SenderCard[] {
  const by = new Map<string, CoverGiven[]>();
  for (const g of given) by.set(g.causeId, [...(by.get(g.causeId) ?? []), g]);
  return [...by.entries()].map(([causeId, items]) => {
    const words = wordsOf.get(causeId) ?? '';
    const res = coverResident(words);
    const meds = items.every((x) => x.itemRef.tab === 'medpass');
    const n = items.length;
    const noun = meds ? (n === 1 ? 'med' : 'meds') : n === 1 ? 'task' : 'tasks';
    const nameOf = (g: CoverGiven) => g.ownerName ?? holder(g);
    const per = new Map<string, number>();
    for (const g of items) per.set(nameOf(g), (per.get(nameOf(g)) ?? 0) + 1);
    const where = res ? `while you’re with ${res}` : 'while you were pulled away';
    const head =
      per.size === 1
        ? `Your ${n} ${noun} went to ${[...per.keys()][0]} ${where}.`
        : `Your ${n} ${noun} were split ${where}: ${[...per.entries()].map(([k, v]) => `${v} to ${k}`).join(', ')}.`;
    const rows = items.map((g) => {
      const l0 = look(g.itemRef);
      const l = { ...l0, who: g.residentName ?? l0.who, task: g.task ?? l0.task };
      const done = g.status === 'done';
      const open = g.status === 'open' || g.status === 'snoozed';
      const by = nameOf(g);
      return {
        key: refKey(g.itemRef),
        ref: g.itemRef,
        who: l.who ? `${l.who}${l.room ? ` · Rm ${l.room}` : ''}` : g.itemRef.tab === 'medpass' ? 'A resident' : 'A task',
        task: l.task ?? (g.itemRef.tab === 'medpass' ? 'Medicine' : 'Task'),
        to: `→ ${by}${g.accepted ? ' · accepted' : ''}`,
        done,
        doneLine: done ? `${g.itemRef.tab === 'medpass' ? 'Given' : 'Done'} by ${by}${g.doneAt ? ` · ${clock(Date.parse(g.doneAt))}` : ''}` : '',
        open,
      };
    });
    const nDone = rows.filter((r) => r.done).length;
    const openN = rows.filter((r) => r.open).length;
    const backHead = openN
      ? `Welcome back. ${nDone} done by your colleagues; ${openN} not done yet. Take back what is not done.`
      : 'Welcome back. Everything is done or back on your list.';
    return { causeId, head, backHead, items: rows, openN, over: items.some((g) => g.causeOpen === false) };
  });
}

// ── The Facility Admin's read-out ───────────────────────────────────────────

export interface Coverage {
  coveredOnTime: { covered: number; onTime: number; pct: number | null };
  live: CoverLive[];
}

/** "Covered on time while pulled away: 98%", worded from the server's 30 days. */
export function coverageShort(c: Coverage | null): string {
  if (!c) return 'Covered on time while pulled away: not connected yet';
  const p = c.coveredOnTime.pct;
  return p === null ? 'Covered on time while pulled away: nothing covered yet' : `Covered on time while pulled away: ${p}%`;
}

/** The staffing summary line. The brief part has no back end yet (shiftBriefFeed). */
export function staffingLine(brief: ShiftBriefFeed | null, c: Coverage | null): string {
  const b = brief ? `${brief.heard} of ${brief.total} heard the brief` : 'shift brief not tracked yet';
  return `Staffing: ${b} · ${coverageShort(c)}`;
}

/** The coverage card's head and its line under it. */
export function coverageCard(c: Coverage | null): { head: string; sub: string } {
  if (!c) return { head: 'Covered on time while staff were pulled away: not connected yet', sub: 'The coverage figure shows here once this building’s cover is connected.' };
  const { covered, onTime, pct } = c.coveredOnTime;
  if (pct === null) return { head: 'Covered on time while staff were pulled away: nothing covered yet', sub: 'Last 30 days · no task has been moved for someone pulled away.' };
  return {
    head: `Covered on time while staff were pulled away: ${pct}%`,
    sub: `Last 30 days · ${onTime} of ${covered} covered ${covered === 1 ? 'task' : 'tasks'} done inside ${covered === 1 ? 'its' : 'their'} window`,
  };
}

/** The live line per emergency still being covered: "Jackie with Edna · 7 tasks covered: 4 Karen, 2 Sam · 0 late". */
export function liveLines(live: CoverLive[], nameOf: (k: string) => string): string[] {
  return live
    .filter((l) => l.total > l.done)
    .map((l) => {
      const s = coverSender(l.words);
      const r = coverResident(l.words);
      const who = s && r ? `${s} with ${r}` : coverFrom(l.words);
      const parts = Object.entries(l.owners).map(([k, v]) => `${v} ${nameOf(k)}`);
      return `${who} · ${l.total} ${l.total === 1 ? 'task' : 'tasks'} covered: ${parts.join(', ')} · ${l.late} late`;
    });
}

// ── New medicine watch ──────────────────────────────────────────────────────

export interface WatchStepRow {
  key: string;
  watchId: string;
  stepId: string;
  residentId: string;
  k: string;
  v: string;
  late: boolean;
}

/** The steps due now or late (design mwWork, MW.tasks): one row per watch, its next step. */
export function watchSteps(watches: MedWatch[], now: number): WatchStepRow[] {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return watches
    .filter((w) => w.status === 'open' && w.next && w.next.status === 'open' && (w.next.late || Date.parse(w.next.dueAt) <= end.getTime()))
    .map((w) => {
      const s = w.next!;
      const due = Date.parse(s.dueAt);
      const when = s.late ? 'late' : Number.isFinite(due) ? clock(due) : s.dueOn;
      return {
        key: `${w.id}:${s.id}`,
        watchId: w.id,
        stepId: s.id,
        residentId: w.residentId,
        k: `${w.residentName ?? 'Resident'}: ${s.label} · due ${when}`,
        v: `${w.medicine ?? w.className}. Why: ${s.why}`,
        late: s.late,
      };
    });
}

/** "New medicine watch: 2 steps are due". */
export function watchHead(n: number): string {
  return n ? `New medicine watch: ${n} ${n === 1 ? 'step is' : 'steps are'} due` : 'New medicine watch: nothing due';
}

export interface WatchSignRow {
  key: string;
  watchId: string;
  residentId: string;
  k: string;
  v: string;
  signs: { key: string; text: string }[];
}

/** Care staff: "Dorothy Meyer · new quetiapine", and what to watch for. */
export function watchSignRows(watches: MedWatchSigns[]): WatchSignRow[] {
  return watches.map((w) => ({
    key: w.id,
    watchId: w.id,
    residentId: w.residentId,
    k: `${w.residentName ?? 'Resident'} · new ${(w.medicine ?? w.className).toLowerCase()}`,
    v: w.signs.map((s) => s.text).join(' · ') + (w.signs.length ? '.' : ''),
    signs: w.signs,
  }));
}

// ── Typed placeholders: no back end yet ─────────────────────────────────────

/** The 60-second shift brief and who heard it (care-ops.js brief). No route records either yet. */
export interface ShiftBriefFeed {
  items: string[];
  heard: number;
  total: number;
  notYet: string[];
}
export const shiftBriefFeed = (): ShiftBriefFeed | null => null;

/** Call-out fill (care-ops.js call): offering a shift to rested staff, then the agency. No route yet. */
export interface CallOutFeed {
  thisMonth: number;
  filledInternally: number;
  avgMinutes: number;
  byAgency: number;
}
export const callOutFeed = (): CallOutFeed | null => null;

/** Medicine safety (med-watch.js discharge, precheck, prn, pharm). No route lists any of these yet. */
export interface MedSafetyFeed {
  discharge: { drug: string; tag: string; why: string }[];
  precheck: { who: string; drug: string; check: string; reading: string | null }[];
  prn: { who: string; drug: string; givenAt: string }[];
  pharmacist: { who: string; rec: string; status: string }[];
}
export const medSafetyFeed = (): MedSafetyFeed | null => null;

/** A care staff member's own license and training hours (V8 Credentials). No route serves them yet. */
export interface MyCredentialsFeed {
  rows: { k: string; v: string }[];
}
export const myCredentialsFeed = (): MyCredentialsFeed | null => null;

/** The survey-readiness items given to one care staff member (V8 Survey ready). No route yet. */
export interface MySurveyFeed {
  rows: { k: string; v: string; done: boolean }[];
}
export const mySurveyFeed = (): MySurveyFeed | null => null;

// ── Pick up where you left off ──────────────────────────────────────────────

/** The Worklist tab this person was last on, in this browser (handoff.js resume, console). A tab name only, never a resident. */
const RESUME_KEY = (userId: string) => `aithora.v9.resume.c.${userId}`;

export function readResume(userId: string | null | undefined): string | null {
  if (!userId) return null;
  try {
    const v = JSON.parse(window.localStorage.getItem(RESUME_KEY(userId)) || 'null') as { tab?: unknown } | null;
    return v && typeof v.tab === 'string' ? v.tab : null;
  } catch {
    return null;
  }
}

export function writeResume(userId: string | null | undefined, tab: string): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(RESUME_KEY(userId), JSON.stringify({ tab }));
  } catch {
    /* a convenience only */
  }
}
